<template>
  <EditionModal :is-displayed="id !== null" :query-param="gwCreationQueryParam" class="gw-creation">
    <div class="gw-creation__content">
      <!-- Description panel -->
      <div class="gw-creation__content__basics">
        <div class="gw-creation__content__basics__info generic-border">
          <h2 class="basic-sub-title">
            {{ $t('message.DESCRIPTION') }}
          </h2>
          <EditableLabel
            ref="name"
            v-model="compGW.name"
            :legend="$t('message.GW_NAME')"
            :placeholder="$t('message.GW_NAME_PLACEHOLDER')"
            @input="save"
            @click.native="$refs.name.doFocus()"
          />
          <EditableLabel
            ref="objective"
            v-model="compGW.objective"
            :legend="$t('message.GW_OBJECTIVE')"
            :placeholder="$t('message.GW_OBJECTIVE_PLACEHOLDER')"
            @input="save"
            @click.native="$refs.objective.doFocus()"
          />
          <IntCheckbox
            v-model="compGW.shouldSetAuditedUser"
            :title="$t('message.GW_SHOULD_SET_AUDITED_USER')"
            @input="save"
          />
          <div class="h-v-centerer">
            <RouterLink
              :to="`/gw-start/${id}`"
              class="generic-border generic-border--text-btn generic-border--blue gw-creation__start"
            >
              <Icon>{{ editBeforeStart }}</Icon>
              <span>{{ $t('message.GW_EDIT_START') }}</span>
            </RouterLink>
          </div>
        </div>
        <div class="gw-creation__content__basics__plannings generic-border">
          <h2 class="basic-sub-title">
            {{ $t('message.PLANNINGS') }}
          </h2>
          <div class="generic-background">
            <GwPlanning
              v-for="(planning, planIndex) in compGW.plannings"
              ref="gwPlannings"
              :key="`${planning.id}-${planIndex}`"
              :planning="planning"
              :index="planIndex"
              @delete="deletePlanning(planning, planIndex)"
            />

            <button
              ref="addPlanning"
              class="
                generic-border
                generic-border--white
                generic-border--text-btn f-w
                gw-creation__content__basics__plannings__add
              "
              @click="addPlanning"
            >
              <div class="generic-border__left">
                <Icon class="generic-border__left-blue-icon">{{ addExtraBold }}</Icon>
                <span>{{ $t('message.ADD_PLANNING') }}</span>
              </div>
              <Icon class="generic-border__right-arrow">{{ arrowDownLine }}</Icon>
            </button>
          </div>
        </div>
      </div>
      <!-- Steps panel -->
      <GwEditionSteplist :gemba-walk="compGW" class="gw-creation__content__steps generic-border" />
    </div>
  </EditionModal>
</template>

<script>
/**
 * GW creation/edition page
 */

import toastr from 'toastr';
import { cloneDeep, pick } from 'lodash';
import { createNamespacedHelpers } from 'vuex';
import {
  SET_GW,
  FETCH_GW,
  SET_GW_PLANNING,
  DELETE_GW_PLANNING,
} from '@/store/modules/gemba-walks/action-types';
// Components
import EditableLabel from '@/components/editable-label.vue';
import EditionModal from '@/components/edition-modals/edition-modal.vue';
import GwEditionSteplist from '@/components/gw-edition-steplist.vue';
import GwPlanning from '@/components/gw-planning.vue';
import Icon from '@/components/icon.vue';
import IntCheckbox from '@/components/int-checkbox.vue';
// Utils
import IAHelp from '@/utils/classes/IAHelp';
import IAHelpStep from '@/utils/classes/IAHelpStep';
import { DEFAULT_REPEAT_DATA } from '@/utils/meeting-utils';
import { addExtraBold, arrowDownLine, editBeforeStart } from '@/assets/icons/';
import { gwCreationQueryParam } from '@/utils/navigation-constants';

const { mapActions: gwMapActions } = createNamespacedHelpers('gembaWalks');

export default {

  name: 'GwCreationModal',

  components: {
    EditableLabel,
    EditionModal,
    GwEditionSteplist,
    GwPlanning,
    Icon,
    IntCheckbox,
  },

  data() {
    return {
      addExtraBold,
      arrowDownLine,
      editBeforeStart,
      gwCreationQueryParam,
      demoGW: {},
      stateGwCopy: {},
      // Properties used by other components. See src/views/readme.md
      pageTitle: this.$t('message.GW_EDITING_TEMPLATE'),
      pageBackButtonAction: () => {
        this.closeModal();
      },
    };
  },

  computed: {
    /** Number: ID of the problem. If 0, creating a new problem */
    id() {
      return this.$route.query[gwCreationQueryParam] === undefined
        ? null
        : Number(this.$route.query[gwCreationQueryParam]);
    },
    iaTeachText() {
      return this.$t(this.currentTeam.hierarchyLevel > 0
        ? 'sensei.BUILD_MUTUAL_TRUST'
        : 'sensei.TEACHER_TEXT');
    },
    iaTeachBtns() {
      return [this.teacherBtn('NO_BLAME'), this.teacherBtn('FIELD_GOLDEN_RULES'), this.teacherBtn('SPOT_PB')];
    },
    /** Object: GW currently displayed (either state GW or demo GW) */
    compGW() {
      return this.demoMode ? this.demoGW : this.stateGwCopy;
    },
    /** Object: GW from state */
    stateGw() {
      return this.$store.state.gembaWalks.gwPerId[`${this.id}`] || {};
    },
  },

  watch: {
    stateGw: {
      deep: true,
      handler() {
        this.stateGwCopy = cloneDeep(this.stateGw);
      },
    },
    id() {
      if (this.id !== null) {
        this.FETCH_GW({ id: this.id });
      } else {
        this.stateGwCopy = {};
      }
    },
  },

  created() {
    if (this.id !== null) {
      this.FETCH_GW({ id: this.id });
    }
  },

  methods: {
    save() {
      const params = pick(this.stateGwCopy, 'id', 'name', 'objective', 'shouldSetAuditedUser');
      this.SET_GW(params).then((response) => {
        if (response && response.data instanceof Object) {
          toastr.success(this.$t('message.GW_SAVED'));
        }
      });
    },
    /** Create a new planning for this tour */
    addPlanning() {
      this.SET_GW_PLANNING({ id: 0, gw: this.id, repeatType: 0, repeatData: DEFAULT_REPEAT_DATA[0] });
    },
    /** Delete a planning of this tour */
    deletePlanning(planning) {
      this.DELETE_GW_PLANNING({ id: planning.id });
    },
    /** Close the modal */
    closeModal() {
      this.$router.back();
    },
    createHelpSteps() {
      this.makeDemoValues();
      return [
        new IAHelpStep(null, this.$t('help.GW_EDITION_INTRO')),
        new IAHelpStep('.gw-creation__content__basics', this.$t('help.GW_EDITION_LEFT')),
        new IAHelpStep('.gw-creation__content__steps', this.$t('help.GW_EDITION_RIGHT')),
        new IAHelpStep('.gw-creation__content__basics__info', this.$t('help.GW_EDITION_NAME_OBJ')),
        new IAHelpStep('.gw-creation__content__steps', this.$t('help.GW_EDITION_STEP_OBJ')),
        new IAHelpStep('.gw-step__details', this.$t('help.GW_EDITION_TEAM')),
        new IAHelpStep('.gw-step .tree-field', this.$t('help.GW_EDITION_ZONE')),
        new IAHelpStep('.gw-step__duration', this.$t('help.GW_EDITION_CL_DURATION')),
        new IAHelpStep('.gw-step', this.$t('help.GW_EDITION_ORDER_STEPS')),
        new IAHelpStep('.gw-step__basics__delete', this.$t('help.GW_EDITION_DEL_STEPS')),
        new IAHelpStep('.gw-step__checklists .editable-label', this.$t('help.GW_EDITION_SELECT_CHECKLIST')),
        new IAHelpStep('.gw-step__checklists__add', this.$t('help.GW_EDITION_ADD_CHECKLIST')),
        new IAHelpStep('.gw-step__checklists .checkbox', this.$t('help.GW_EDITION_REMOVE_CHECKLIST')),
        new IAHelpStep('.gw-step__checklists__selected .icon', this.$t('help.GW_EDITION_EDIT_CHECKLISTS')),
        new IAHelpStep(this.$refs.addPlanning, this.$t('help.GW_EDITION_ADD_PLANNING'), (() => {
          if (this.$refs.gwPlannings && this.$refs.gwPlannings[0]) {
            if (this.$refs.gwPlannings[0].planningExpanded !== undefined) {
              this.$refs.gwPlannings[0].planningExpanded = true;
            }
          }
        })),
        new IAHelpStep('.gw-planning .participants', this.$t('help.GW_EDITION_PLAN_PARTICIPANTS')),
        new IAHelpStep('.gw-planning', this.$t('help.GW_EDITION_PLAN_SCHEDULE')),
        new IAHelpStep('.gw-creation__start', this.$t('help.GW_EDITION_START')),
      ];
    },
    makeDemoValues() {
      this.demoGW = IAHelp.demoGWTemplate();
    },
    ...gwMapActions({
      SET_GW,
      FETCH_GW,
      SET_GW_PLANNING,
      DELETE_GW_PLANNING,
    }),
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.gw-creation {
  overflow: auto;

  &__content {
    padding: $page-content-padding;
    display: flex;

    &__basics__info, &__basics__plannings, &__steps {
      padding: $page-content-padding;

      h2.basic-sub-title {
        margin-top: 0;
      }
    }

    &__basics {
      flex: 0 0 30%;
      width: 30%;
      margin-right: $page-content-padding;
      display: flex;
      flex-flow: column;

      &__info {

        .editable-label {
          padding: $page-content-padding;
          border-radius: $main-border-radius;
          cursor: pointer;

          &:hover {
            background: $main-hover-color;
          }
        }
      }

      &__plannings {
        margin-top: $page-content-padding;

        &__add {
          margin-top: 1.5rem;
        }
      }
    }

    &__steps {
      flex: 1;
      min-width: 0px;
    }
  }

  &__start {
    margin-top: 1rem;
    margin-bottom: 2rem;
  }
}


@media (max-width: $smartphone-landscape) {
  .gw-creation {
    &__content {
      flex-flow: column;

      &__basics {
        margin-right: 0px;
        margin-bottom: .7rem;
        flex: 1;
        width: auto;
      }
    }
  }
}
</style>
